import { Icon } from "./Icon";
import { Empty, PageHeading, Ring } from "./ui";
import { LevelProgress } from "./LevelProgress";
import { useT } from "../lib/i18n";
import type { Level, Term } from "../lib/curriculum";
import type { Activity, PlanDay, PlanItem } from "../types";

export type PlannedMoment = { item: PlanItem; activity: Activity };

/** Pair each plan item with its activity, dropping any the library no longer has. */
export function momentsFor(plan: PlanDay | null, activities: Activity[]): PlannedMoment[] {
  if (!plan) return [];
  const byId = new Map(activities.map((activity) => [activity.id, activity]));
  return plan.items.flatMap((item) => {
    const activity = byId.get(item.activity_id);
    return activity ? [{ item, activity }] : [];
  });
}

/**
 * The day's six, and the optional extras, told apart.
 *
 * Both are real plan items — that is what a tick and a piece of evidence hang
 * off — so the only thing keeping the extras optional is that everything which
 * counts uses `core`. Split once, at the top, rather than filtering at each of
 * the dozen places that read a length: the failure mode of missing one is a
 * progress ring that reads 6/8 and a family who reasonably conclude they are
 * behind.
 */
export function splitMoments(moments: PlannedMoment[]): {
  core: PlannedMoment[];
  extras: PlannedMoment[];
} {
  return {
    core: moments.filter((moment) => moment.activity.track !== "enrichment"),
    extras: moments.filter((moment) => moment.activity.track === "enrichment"),
  };
}

export function ActivityCard({
  moment,
  order,
  onOpen,
  onEvidence,
  onToggle,
}: {
  moment: PlannedMoment;
  order?: number;
  onOpen: () => void;
  /** Absent for an account that may not record for this child (a coach). */
  onEvidence?: () => void;
  /**
   * The one-tap tick. The home page used to have none, so saying an activity
   * happened meant opening it and scrolling to the bottom — and almost nobody
   * did, which left the curriculum standing still.
   */
  onToggle?: () => void;
}) {
  const t = useT();
  const { item, activity } = moment;
  const complete = item.status === "done";
  return (
    <article className={`activity-card glass-card ${complete ? "completed" : ""}`} data-color={activity.color}>
      <div className="activity-top">
        <span className={`icon-tile ${activity.color}`}>
          <Icon name={activity.icon} />
        </span>
        {order !== undefined && <span className="activity-number" aria-hidden="true">{String(order).padStart(2, "0")}</span>}
        <span className="time">
          <Icon name="clock" size={14} />
          {t("today.minutes", { count: activity.duration_minutes })}
        </span>
      </div>
      <small>{activity.area_label ?? activity.area}</small>
      <h3>{activity.title}</h3>
      <p>{activity.prompt}</p>
      <div className="activity-actions">
        {complete ? (
          <button className="complete-state" onClick={onOpen}>
            <Icon name="check" /> {t("today.doneToday")}
          </button>
        ) : (
          <button className="start-link" onClick={onOpen}>
            {t("today.openActivity")} <Icon name="arrow" />
          </button>
        )}
        <span className="card-tools">
          {onToggle && (
            <button
              className={complete ? "round-check done" : "round-check"}
              onClick={onToggle}
              aria-pressed={complete}
              aria-label={
                complete
                  ? t("today.markNotDone", { title: activity.title })
                  : t("today.markComplete", { title: activity.title })
              }
            >
              <Icon name="check" />
            </button>
          )}
          {onEvidence && (
            <button
              className="evidence-button"
              onClick={onEvidence}
              aria-label={t("today.evidenceFor", { title: activity.title })}
            >
              <Icon name="camera" />
            </button>
          )}
        </span>
      </div>
    </article>
  );
}

export function TodayPage({
  learnerName,
  moments,
  extras = [],
  level,
  terms,
  daysWorked,
  onOpen,
  onToggle,
}: {
  learnerName: string;
  moments: PlannedMoment[];
  /** Offered beside the day, never counted towards it. */
  extras?: PlannedMoment[];
  /** Where this child is in the curriculum. Null before a level is chosen. */
  level: Level | null;
  terms: Term[];
  daysWorked: number;
  onOpen: (moment: PlannedMoment) => void;
  /** Absent for an account that may not record for this child. */
  onToggle?: (itemId: string) => void;
}) {
  const t = useT();
  const done = moments.filter((moment) => moment.item.status === "done").length;
  const progress = moments.length ? Math.round((done / moments.length) * 100) : 0;

  return (
    <>
      <PageHeading
        eyebrow={t("today.eyebrow")}
        title={t("home.todayWith", { name: learnerName })}
        copy={t("today.copy")}
        action={
          <div className="heading-stat glass-card">
            <Ring value={progress} size={60} />
            <div>
              <strong>
                {done}/{moments.length}
              </strong>
              <small>{t("home.momentsComplete")}</small>
            </div>
          </div>
        }
      />
      <LevelProgress level={level} terms={terms} daysWorked={daysWorked} />
      {moments.length === 0 ? (
        <Empty icon="calendar" title={t("today.emptyTitle")} copy={t("today.emptyCopy")} />
      ) : (
        <div className="day-timeline">
          {moments.map((moment, index) => (
            <article
              className={`timeline-item glass-card ${moment.item.status === "done" ? "completed" : ""}`}
              key={moment.item.id}
            >
              <div className="timeline-time">
                <span>
                  {index < 2 ? t("today.morning") : index < 4 ? t("today.afternoon") : t("today.anytime")}
                </span>
                <i />
              </div>
              <span className={`icon-tile ${moment.activity.color}`}>
                <Icon name={moment.activity.icon} />
              </span>
              <div className="timeline-copy">
                <small>
                  {moment.activity.area_label ?? moment.activity.area} · {t("today.minutes", { count: moment.activity.duration_minutes })}
                </small>
                <h3>{moment.activity.title}</h3>
                <p>{moment.activity.prompt}</p>
                <div className="plain-tags">
                  {moment.activity.child_move && (
                    <span>
                      {t("today.child")}: {moment.activity.child_move}
                    </span>
                  )}
                  {moment.activity.parent_move && (
                    <span>
                      {t("today.parent")}: {moment.activity.parent_move}
                    </span>
                  )}
                </div>
              </div>
              <div className="timeline-actions">
                <button className="soft-button" onClick={() => onOpen(moment)}>
                  {t("today.viewGuide")}
                </button>
                {onToggle && <button
                  className={moment.item.status === "done" ? "round-check done" : "round-check"}
                  onClick={() => onToggle(moment.item.id)}
                  aria-pressed={moment.item.status === "done"}
                  aria-label={
                    moment.item.status === "done"
                      ? t("today.markNotDone", { title: moment.activity.title })
                      : t("today.markComplete", { title: moment.activity.title })
                  }
                >
                  <Icon name="check" />
                </button>}
              </div>
            </article>
          ))}
        </div>
      )}

      {/* Below the day, deliberately.
          Above it, or interleaved with it, and "optional" becomes a suggestion
          a conscientious parent feels they are failing. The heading says what
          these are before anyone has to infer it from the styling. */}
      {extras.length > 0 ? (
        <section className="day-extras" aria-labelledby="day-extras-heading">
          <div className="day-extras-head">
            <h2 id="day-extras-heading">{t("today.extrasTitle")}</h2>
            <p>{t("today.extrasCopy")}</p>
          </div>
          <div className="extras-grid">
            {extras.map((moment) => (
              <article
                key={moment.item.id}
                className={`extra-card glass-card ${moment.item.status === "done" ? "completed" : ""}`}
                data-color={moment.activity.color}
              >
                <div className="extra-top">
                  <span className={`icon-tile ${moment.activity.color}`}>
                    <Icon name={moment.activity.icon} />
                  </span>
                  <span className="extra-area">{moment.activity.area_label ?? moment.activity.area}</span>
                  <span className="time">
                    <Icon name="clock" size={14} />
                    {t("today.minutes", { count: moment.activity.duration_minutes })}
                  </span>
                </div>
                <h3>{moment.activity.title}</h3>
                <p>{moment.activity.prompt}</p>
                <div className="extra-actions">
                  <button className="soft-button" onClick={() => onOpen(moment)}>
                    {t("today.viewGuide")}
                  </button>
                  {onToggle && <button
                    className={moment.item.status === "done" ? "round-check done" : "round-check"}
                    onClick={() => onToggle(moment.item.id)}
                    aria-pressed={moment.item.status === "done"}
                    aria-label={
                      moment.item.status === "done"
                        ? t("today.markNotDone", { title: moment.activity.title })
                        : t("today.markComplete", { title: moment.activity.title })
                    }
                  >
                    <Icon name="check" />
                  </button>}
                </div>
              </article>
            ))}
          </div>
        </section>
      ) : null}
    </>
  );
}
